import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "app/services/abstract.service";
import { ICreateDocumentClass } from "./interfaces/create-document-class.interface";
import { IUpdateDocumentClass } from "./interfaces/update-document-class.interface";
import DocumentClassAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/documents/document-class/document-class.entity";
const apiUrl = env.API_DOCUMENT_URL;
const moduleName = "document_class";

@Injectable({
  providedIn: "root",
})
export class DocumentClassService extends AbstractService<
  DocumentClassAttributes,
  ICreateDocumentClass,
  IUpdateDocumentClass
> {
  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene clases de documento con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param companyId Company filter
   * @param status Status filter
   * @param typeDocuments Type documents filter
   * @returns Paginated data
   *
   * Search fields: code, name
   * Array filters: status, typeDocuments
   * Single filters: companyId
   */
  findAllWithCount(
    options: IPagination<DocumentClassAttributes>,
    companyId?: number,
    status?,
    typeDocuments?: string[]
  ): Observable<IPaginationResult<DocumentClassAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, estados, tipos de documento.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<DocumentClassAttributes>[] => {
      const deletedFilter: FilterFn<DocumentClassAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        companyId
          ? andWhere('companies.id' as keyof DocumentClassAttributes, Ops.eq(companyId))
          : noop(),
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop(),
        typeDocuments && typeDocuments.length > 0
          ? andWhere('documentType.code' as keyof DocumentClassAttributes, Ops.in(...typeDocuments))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<DocumentClassAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<DocumentClassAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<DocumentClassAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<DocumentClassAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(
    documentTypeId?: string | string[],
    documentTypeCode?: string | string[]
  ): Observable<IRequestResult<DocumentClassAttributes[]>> {
    const query = buildQuery<DocumentClassAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      documentTypeId && documentTypeId.length > 0
        ? Array.isArray(documentTypeId)
          ? andAllWhere(
            "documentType._id" as keyof DocumentClassAttributes,
            Ops.in(...documentTypeId)
          )
          : andAllWhere(
            "documentType._id" as keyof DocumentClassAttributes,
            Ops.eq(documentTypeId)
          )
        : noop(),
      documentTypeCode && documentTypeCode.length > 0
        ? Array.isArray(documentTypeCode)
          ? andAllWhere(
            "documentType.code" as keyof DocumentClassAttributes,
            Ops.in(...documentTypeCode)
          )
          : andAllWhere(
            "documentType.code" as keyof DocumentClassAttributes,
            Ops.eq(documentTypeCode)
          )
        : noop(),

      select(["code", "documentType", "name", "actionsRequirements"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<DocumentClassAttributes[]>>(API_URL);
  }

  /**
   * Find to combo box for document class by document type code in company
   * @returns Data
   */
  findToComboBoxByCode(
    documentTypeCode?: string | string[]
  ): Observable<IRequestResult<DocumentClassAttributes[]>> {
    const query = buildQuery<DocumentClassAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      documentTypeCode && documentTypeCode.length > 0
        ? Array.isArray(documentTypeCode)
          ? andAllWhere(
            "documentType.code" as keyof DocumentClassAttributes,
            Ops.in(...documentTypeCode)
          )
          : andAllWhere(
            "documentType.code" as keyof DocumentClassAttributes,
            Ops.eq(documentTypeCode)
          )
        : noop(),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<DocumentClassAttributes[]>>(API_URL);
  }
}
